import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type UIEventHandler
} from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import {
  PIN_AWARE_BOTTOM_THRESHOLD_PX,
  readDistanceFromBottom,
  readPinAwareBottomTop
} from '@/lib/chat/scroll-geometry'

type ScrollParams = {
  ref?: HTMLDivElement | null
  pinToTop?: boolean
  scrollToMessageId?: string | null
  /** Sticky composer height — last message must clear this when settling. */
  composerInset?: number
}

const PROGRAMMATIC_SCROLL_LOCK_MS = 700

const VALID_SCROLL_BEHAVIORS = new Set<ScrollBehavior>([
  'auto',
  'smooth',
  'instant'
])

/**
 * Click handlers often pass the event as the first arg when wired as
 * `onClick={scrollToBottom}`. Passing that object into Element.scrollTo as
 * `behavior` throws TypeError on Safari ("Type error").
 */
function resolveScrollBehavior(
  behavior: unknown,
  fallback: ScrollBehavior
): ScrollBehavior {
  return typeof behavior === 'string' &&
    VALID_SCROLL_BEHAVIORS.has(behavior as ScrollBehavior)
    ? (behavior as ScrollBehavior)
    : fallback
}

function safeElementScrollTo(
  el: HTMLElement,
  options: ScrollToOptions
): void {
  const top =
    typeof options.top === 'number' && Number.isFinite(options.top)
      ? Math.max(0, options.top)
      : 0
  const behavior = resolveScrollBehavior(options.behavior, 'auto')

  try {
    el.scrollTo({ top, behavior })
  } catch {
    // Safari / non-standard hosts: fall back to instant assignment.
    el.scrollTop = top
  }
}

// Base hook for scroll management, without any chat-specific logic
export const useScrollBase = (
  scrollParams: ScrollParams = {
    ref: null
  },
  isGenerating: boolean = false
) => {
  const startRef = useRef<HTMLDivElement>(scrollParams.ref ?? null)
  const endRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(scrollParams.ref ?? null)
  const isAutoScrolling = useRef(false)
  const programmaticScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const [isAtTop, setIsAtTop] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [userScrolled, setUserScrolled] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [shouldScrollToMessage, setShouldScrollToMessage] = useState(false)

  const pinToTop = scrollParams?.pinToTop === true
  const composerInset =
    typeof scrollParams?.composerInset === 'number' &&
    Number.isFinite(scrollParams.composerInset)
      ? Math.max(0, scrollParams.composerInset)
      : 0

  const measureDistance = useCallback(() => {
    if (!scrollRef.current) return 0
    return readDistanceFromBottom(
      scrollRef.current,
      endRef.current,
      pinToTop,
      composerInset
    )
  }, [pinToTop, composerInset])

  const checkIsAtBottom = useCallback(() => {
    if (!scrollRef.current) return
    setIsAtBottom(measureDistance() <= PIN_AWARE_BOTTOM_THRESHOLD_PX)
  }, [measureDistance])

  const beginProgrammaticScroll = useCallback(
    (lockMs: number = PROGRAMMATIC_SCROLL_LOCK_MS) => {
      isAutoScrolling.current = true
      if (programmaticScrollTimeoutRef.current) {
        clearTimeout(programmaticScrollTimeoutRef.current)
      }
      programmaticScrollTimeoutRef.current = setTimeout(() => {
        isAutoScrolling.current = false
        programmaticScrollTimeoutRef.current = null
        checkIsAtBottom()
      }, lockMs)
    },
    [checkIsAtBottom]
  )

  useEffect(() => {
    return () => {
      if (programmaticScrollTimeoutRef.current) {
        clearTimeout(programmaticScrollTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const currentRef = scrollRef.current
    if (!currentRef) return

    const resizeObserver = new ResizeObserver(() => {
      checkIsAtBottom()
    })
    resizeObserver.observe(currentRef)
    const spacer = endRef.current
    if (spacer) {
      resizeObserver.observe(spacer)
    }
    return () => {
      resizeObserver.disconnect()
    }
  }, [checkIsAtBottom])

  // Composer inset / spacer size changes move the true bottom — remeasure.
  useEffect(() => {
    checkIsAtBottom()
  }, [composerInset, checkIsAtBottom])

  const handleScroll: UIEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      const target = e.target as HTMLDivElement
      const isOverflow = target.scrollHeight > target.clientHeight
      setIsOverflowing(isOverflow)
      const atBottom =
        readDistanceFromBottom(target, endRef.current, pinToTop, composerInset) <=
        PIN_AWARE_BOTTOM_THRESHOLD_PX
      checkIsAtBottom()
      setIsAtTop(target.scrollTop === 0)
      // Only update userScrolled when scroll was user-initiated (not pin /
      // scrollToBottom / scrollIntoView we own). Otherwise pin-to-top marks the
      // user as having scrolled away and the post-stream settle is skipped —
      // leaving the thread parked over the empty spacer void.
      if (!isAutoScrolling.current) {
        if (!atBottom) {
          setUserScrolled(true)
        } else {
          setUserScrolled(false)
        }
      }
    },
    [checkIsAtBottom, pinToTop, composerInset]
  )

  const scrollToTop = useCallback(() => {
    if (!scrollRef.current) return
    beginProgrammaticScroll()
    safeElementScrollTo(scrollRef.current, {
      top: 0,
      behavior: 'smooth'
    })
  }, [beginProgrammaticScroll])

  const scrollToBottom = useCallback(
    (behavior?: unknown) => {
      if (!scrollRef.current || shouldScrollToMessage) return

      beginProgrammaticScroll()
      // Pin-to-top: never scroll to raw scrollHeight (spacer void). Align the
      // end of real content just above the sticky composer.
      const targetTop = readPinAwareBottomTop(
        scrollRef.current,
        endRef.current,
        pinToTop,
        composerInset
      )
      // Callers (chat open) pass 'auto'. Click handlers must NOT pass the
      // MouseEvent through as behavior — browsers throw TypeError on that.
      safeElementScrollTo(scrollRef.current, {
        top: targetTop,
        behavior: resolveScrollBehavior(
          behavior,
          isGenerating ? 'auto' : 'smooth'
        )
      })
      // Mark bottom immediately so the overlay clears without waiting for the
      // smooth-scroll settle (that lag made the button feel broken).
      setIsAtBottom(true)
      setUserScrolled(false)
    },
    [
      shouldScrollToMessage,
      isGenerating,
      pinToTop,
      composerInset,
      beginProgrammaticScroll
    ]
  )

  const scrollToMessage = useCallback(
    (messageRef: React.RefObject<HTMLDivElement | null>) => {
      if (!messageRef.current) return
      beginProgrammaticScroll()
      messageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
      setShouldScrollToMessage(false)
    },
    [beginProgrammaticScroll]
  )

  /**
   * Pin a message node near the top of the scroller, respecting the sticky
   * mobile header (two-row title + actions) via scroll-padding-top.
   * Must go through the same programmatic lock as scrollToBottom so
   * handleScroll does not treat the pin as a user interrupt.
   */
  const pinElementToTop = useCallback(
    (el: HTMLElement | null | undefined) => {
      if (!el) return
      const scrollEl = scrollRef.current
      beginProgrammaticScroll()
      if (scrollEl) {
        const styles = window.getComputedStyle(scrollEl)
        const padTop = Number.parseFloat(styles.scrollPaddingTop || '0') || 0
        const elRect = el.getBoundingClientRect()
        const scrollRect = scrollEl.getBoundingClientRect()
        const absoluteTop =
          elRect.top - scrollRect.top + scrollEl.scrollTop
        const nextTop = Math.max(0, absoluteTop - padTop)
        safeElementScrollTo(scrollEl, { top: nextTop, behavior: 'smooth' })
        return
      }
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    },
    [beginProgrammaticScroll]
  )

  return {
    scrollRef,
    startRef,
    endRef,
    isAtTop,
    isAtBottom,
    userScrolled,
    isOverflowing,
    handleScroll,
    scrollToTop,
    scrollToBottom,
    scrollToMessage,
    pinElementToTop,
    beginProgrammaticScroll,
    setShouldScrollToMessage,
    setIsAtBottom,
    setUserScrolled,
    checkIsAtBottom,
    getDistanceFromBottom: measureDistance
  }
}

export const useScroll = (scrollParams: ScrollParams = {}) => {
  const { isGenerating, chatMessages } = useContext(ChatbotUIChatContext)
  const {
    scrollRef,
    startRef: messagesStartRef,
    endRef: messagesEndRef,
    isAtTop,
    isAtBottom,
    userScrolled,
    isOverflowing,
    handleScroll,
    scrollToTop,
    scrollToBottom,
    scrollToMessage: baseScrollToMessage,
    pinElementToTop,
    beginProgrammaticScroll,
    setIsAtBottom,
    setUserScrolled,
    checkIsAtBottom,
    getDistanceFromBottom
  } = useScrollBase(scrollParams, isGenerating)

  const [, setShouldScrollToHighlight] = useState(false)

  // When pinToTop is enabled (main chat), the newest USER turn is pinned to
  // the top on send by chat-messages. That pin is the only send scroll —
  // nothing re-scrolls when the stream finishes or the spacer shrinks.
  const pinToTop = scrollParams?.pinToTop === true

  // Reset userScrolled when generation starts (user can interrupt again)
  useEffect(() => {
    if (isGenerating) {
      setUserScrolled(false)
    }
  }, [isGenerating, setUserScrolled])

  // THE only autoscroll: when the user sends a message. Streaming tokens,
  // generation end, and later message-list updates must never scroll on
  // their own — the old follow-the-stream + post-generation settle fought
  // the send pin (scroll, undo, repeated jumps). Pin-to-top surfaces pin
  // the sent bubble via chat-messages instead, so this covers non-pin
  // surfaces (split view, public) with one scroll per send.
  const lastSentUserMessageIdRef = useRef<string | null>(null)
  const sendScrollPrimedRef = useRef(false)
  const scrollToMessageId = scrollParams?.scrollToMessageId ?? null

  useEffect(() => {
    if (pinToTop) return
    if (scrollToMessageId) return
    if (!chatMessages || chatMessages.length === 0) return

    let lastUserMessageId: string | null = null
    for (let index = chatMessages.length - 1; index >= 0; index -= 1) {
      const entry = chatMessages[index] as unknown as {
        message?: { id?: string; role?: string }
        id?: string
        role?: string
      }
      const role = entry?.message?.role ?? entry?.role
      if (role === 'user') {
        lastUserMessageId = entry?.message?.id ?? entry?.id ?? null
        break
      }
    }

    // First observed list is chat open, not a send — park without scrolling.
    if (!sendScrollPrimedRef.current) {
      sendScrollPrimedRef.current = true
      lastSentUserMessageIdRef.current = lastUserMessageId
      return
    }

    if (
      !lastUserMessageId ||
      lastUserMessageId === lastSentUserMessageIdRef.current
    ) {
      return
    }
    lastSentUserMessageIdRef.current = lastUserMessageId

    requestAnimationFrame(() => {
      scrollToBottom()
    })
  }, [chatMessages, pinToTop, scrollToMessageId, scrollToBottom])

  const scrollToMessage = useCallback(
    (messageRef: React.RefObject<HTMLDivElement | null>) => {
      baseScrollToMessage(messageRef)
      setShouldScrollToHighlight(false)
    },
    [baseScrollToMessage]
  )

  return {
    scrollRef,
    messagesStartRef,
    messagesEndRef,
    isAtTop,
    isAtBottom,
    userScrolled,
    isOverflowing,
    handleScroll,
    scrollToTop,
    scrollToBottom,
    scrollToMessage,
    pinElementToTop,
    beginProgrammaticScroll,
    setShouldScrollToHighlight,
    setIsAtBottom,
    setUserScrolled,
    getDistanceFromBottom
  }
}
